import { Card, Col, message, Row, Spin } from "antd";
import { Content } from "antd/es/layout/layout";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import OrderTotals from "./OrderTotals";
import OrderSummary from "./OrderSummary";
import { AuthContext } from "../../context/AuthProvider";
import { ordersService } from "../../services";
import OrderHeader from "./OrderHeader";
import ItemDetailsModal from "./item-details/ItemDetailsModal";
import moment from "moment";
import {
  downloadDataAsCSV,
  downloadDataAsXLSX,
} from "../../utils/downloadFile";


interface NavState {
  templateId?: string;
  excludeErrors?: boolean;
}

interface NavState {
  templateId?: string;
  excludeErrors?: boolean;
}

const OrderPage: React.FC = () => {
  const defaultEnd = moment();
  const defaultStart = moment().startOf("month");
  const { user, selectedSwitcherAccountId } = useContext(AuthContext);
  const location = useLocation();
  const navState = location.state as NavState | null;
  const [loading, setLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [filters, setFilters] = useState<any>({
    template: navState?.templateId || null,
    size: null,
    deliveryMethod: null,
    dates: [
      [defaultStart.format("MM/DD/YYYY"), defaultEnd.format("MM/DD/YYYY")],
      "mtd",
    ],
    status: navState?.excludeErrors ? 'Error' : null,
    search: null,
  });
  const [orderList, setOrderList] = useState<any>([]);
  const [orderSummary, setOrderSummary] = useState<any>(null);
  const [templates, setTemplates] = useState<any>([]);
  const [sizes, setSizes] = useState<any>([]);
  const [deliveryMethods, setDeliveryMethods] = useState<any>([]);
  const [deliveryStatus, setDeliveryStatus] = useState<any>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25 });
  const [totalOrders, setTotalOrders] = useState(0);
  const [sort, setSort] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({
    field: "dateSubmitted",
    order: "desc",
  });

  const [orderMenuResetKey, setOrderMenuResetKey] = useState(0);
  const previousSwitcherAccountId = useRef(selectedSwitcherAccountId);
  useEffect(() => {
    if (previousSwitcherAccountId.current === selectedSwitcherAccountId) {
      return;
    }
    previousSwitcherAccountId.current = selectedSwitcherAccountId;
    const resetStart = moment().startOf("month");
    const resetEnd = moment();
    setFilters({
      template: null,
      size: null,
      deliveryMethod: null,
      dates: [
        [resetStart.format("MM/DD/YYYY"), resetEnd.format("MM/DD/YYYY")],
        "mtd",
      ],
      status: null,
      search: null,
    });
    setPagination({ page: 1, limit: 25 });
    setOrderMenuResetKey((k) => k + 1);
  }, [selectedSwitcherAccountId]);

  const columns = [
    { title: "ITEM REF ID", dataIndex: "itemReferenceId" },
    { title: "ITEM ID", dataIndex: "itemId" },
    { title: "XD ORDER ID", dataIndex: "xdOrderId" },
    { title: "DATE SUBMITTED", dataIndex: "dateSubmitted" },
    { title: "FULFILLMENT DATE", dataIndex: "fulfillmentDate" },
    { title: "REQUESTED QTY.", dataIndex: "quantity" },
    { title: "FINAL QTY.", dataIndex: "recipients" },
    { title: "ORDER TOTAL", dataIndex: "orderTotal" },
    { title: "ORDER STATUS", dataIndex: "orderStatus" },
    { title: "DELIVERY STATUS", dataIndex: "deliveryStatus" },
    { title: "SIZE", dataIndex: "size" },
    { title: "DELIVERY METHOD", dataIndex: "deliveryMethod" },
    { title: "TEMPLATE", dataIndex: "templateName" },
  ];

  const prepareData = (data: any) => {
    const headers = columns.map((col) => col.title);

    let rows = data.map((row: any) =>
      columns.map((col) => {
        if (col.dataIndex === "templateName") {
          return row.template?.templateName ?? "-";
        }
        if (col.dataIndex === "size") {
          return row.template?.size ?? "-";
        }
        if (col.dataIndex === "recipients") {
          return row.recipients?.total? row.recipients.total.toLocaleString() : "-";
        }
        if (col.dataIndex === "orderTotal") {
          return row.orderTotal ? row.orderTotal.toLocaleString() : "-";
        }
        if (col.dataIndex === "quantity") {
          return row.quantity?  row.quantity.toLocaleString() : "-";
        }
        return row[col.dataIndex] ?? "";
      }),
    );
    const result = rows.map((row: any) =>
      Object.fromEntries(headers.map((header, index) => [header, row[index]])),
    );
    rows = result;
    return rows;
  };

  const handleExport = async (
    fileFormat: "CSV" | "EXCEL",
    type = "CURRENT",
  ) => {
    let data = [...orderList];
    if (type === "ALL") {
      const dataUrl = await getOrderData(true);
      if (!dataUrl) {
        message.error("Something gone wrong while trying to export data.");
        return;
      }
      try {
        const response = await fetch(dataUrl);

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const json = await response.json();

        data = json.data;
      } catch (err) {
        message.error("Something gone wrong while trying to export data.");
        return;
      }
    }

    const rows = prepareData(data);
    if (!rows?.length) {
      message.error("There is no data to export.");
      return;
    }
    if (fileFormat === "CSV") {
      downloadDataAsCSV(rows, `orderList_${Date.now()}.csv`);
    } else {
      downloadDataAsXLSX(rows, `orderList_${Date.now()}.xlsx`);
    }
  };

  const getOrderData = async (exportData: boolean) => {
    try {
      setLoading(true);
      const payload: any = {
        accountId: selectedSwitcherAccountId || user?.accountId || "",
      };
      if (filters?.dates?.[0]?.length) {
        payload.startDate = moment(filters.dates[0][0]).format("YYYY-MM-DD");
        payload.endDate = moment(filters.dates[0][1]).format("YYYY-MM-DD");
      }
      if (filters?.size) {
        payload.size = filters.size;
      }
      if (filters?.deliveryMethod) {
        payload.deliveryMethod = filters.deliveryMethod;
      }
      if (filters?.status) {
        payload.deliveryStatus = filters.status;
      }
      if (filters?.search) {
        payload.search = filters.search;
      }
      if (filters?.template) {
        payload.templateIds = filters.template;
      }
      
      if (exportData) {
        payload.export = true;
      } else {
        payload.page = pagination.page;
        payload.limit = pagination.limit;
      }

      if (sort.field) {
        payload.sortBy =
          sort.field === "recipients" ? "validRecipients" : sort.field;
        payload.sortOrder = sort.order;
      }
      const data: any = await ordersService.getOrdersList(payload);
      if (!exportData) {
        setOrderList(data?.data || []);
        setOrderSummary(data?.summary || null);
        setTotalOrders(data?.pagination?.totalItems ?? 0);
      }
      return data?.url;
    } catch (e: any) {
      message.error(e?.message || "Something went wrong when fetching data");
      return "";
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    getOrderData(false);
    // eslint-disable-next-line
  }, [filters, pagination, sort, user, selectedSwitcherAccountId]);

  useEffect(() => {
    if (!user) return;
    const fetchFilterOptions = async () => {
      try {
        const data = await ordersService.getOrderFilters(selectedSwitcherAccountId || user.accountId);
        setTemplates(
          (data?.templates || []).map((t: any) => ({ key: t.templateId, label: t.templateName }))
        );
        setDeliveryMethods(
          (data?.deliveryMethod || []).filter((f: string) => f !== '*').map((v: string) => ({ key: v, label: v }))
        );
        setDeliveryStatus(
          (data?.deliveryStatus || []).map((v: string) => ({ key: v, label: v }))
        );
        setSizes(
          (data?.size || []).map((v: string) => ({ key: v, label: v }))
        );
      } catch (e: any) {
        message.error(e?.message || "Something went wrong when fetching filter options");
      }
    };
    fetchFilterOptions();
  }, [user, selectedSwitcherAccountId]);

  return (
    <Content
      style={{
        padding: "20px",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Spin spinning={loading} fullscreen />
      <div style={{ flexShrink: 0 }}>
        <OrderHeader
          key={orderMenuResetKey}
          sizes={sizes}
          deliveryMethods={deliveryMethods}
          deliveryStatus={deliveryStatus}
          templates={templates}
          setFilters={(f: any) => {
            setPagination((prev) => ({ ...prev, page: 1 }));
            setFilters(f);
          }}
          filters={filters}
          onExport={handleExport}
        />
      </div>
      <Row gutter={[16, 16]} style={{ flexShrink: 0 }}>
        <Col xs={24} md={8} lg={8} >
          <Card
            style={{
              height: "100%",
              overflow: "auto",
              padding: 20,
              boxShadow: "none",
            }}
            variant="borderless"
          >
            <OrderTotals
              headerText="TOTAL ITEMS ORDERED"
              icon={1}
              total={
                orderSummary?.totalItemsOrdered?.toLocaleString("en-US") || null
              }
            />
          </Card>
        </Col>

        <Col xs={24} md={8} lg={8}>
          <Card
            style={{ height: "100%", overflow: "auto", boxShadow: "none", padding: 20 }}
            variant="borderless"
          >
            <OrderTotals
              headerText="TOTAL ITEMS W/ERRORS"
              icon={2}
              total={
                orderSummary?.totalItemsWithErrors?.toLocaleString("en-US") ||
                null
              }
            />
          </Card>
        </Col>
        <Col xs={24} md={8} lg={8} >
          <Card
            style={{ height: "100%", overflow: "auto", boxShadow: "none",padding: 20 }}
            variant="borderless"
          >
            <OrderTotals
              headerText="TOTAL PIECES SENT"
              icon={3}
              total={orderSummary?.totalPieces?.toLocaleString("en-US") || null}
            />
          </Card>
        </Col>
      </Row>
      <Row
        gutter={[16, 16]}
        style={{ paddingTop: "16px", flex: 1, minHeight: 0 }}
      >
        <Col xs={24} style={{ height: "100%" }}>
          <OrderSummary
            orders={orderList}
            pagination={pagination}
            totalOrders={totalOrders}
            onPaginationChange={(page: number, limit: number) =>
              setPagination({ page, limit })
            }
            sortField={sort.field}
            sortOrder={sort.order}
            onSortChange={(field, order) => {
              setPagination((prev) => ({ ...prev, page: 1 }));
              setSort({ field, order });
            }}
            onRowClick={setSelectedItemId}
          />
        </Col>
      </Row>

      <ItemDetailsModal
        open={selectedItemId !== null}
        itemId={selectedItemId}
        onClose={() => setSelectedItemId(null)}
      />
    </Content>
  );
};

export default OrderPage;
